import { Point } from '../model/calepinage.types'

export class CalepinageGeometry {
    static toRadians(deg: number): number {
        return (deg * Math.PI) / 180
    }

    static rotate(p: Point, angleRad: number, origin: Point = { x: 0, y: 0 }): Point {
        const cos = Math.cos(angleRad)
        const sin = Math.sin(angleRad)
        const dx = p.x - origin.x
        const dy = p.y - origin.y
        return {
            x: origin.x + dx * cos - dy * sin,
            y: origin.y + dx * sin + dy * cos,
        }
    }

    static rotatePolygon(poly: Point[], angleRad: number, origin: Point = { x: 0, y: 0 }): Point[] {
        return poly.map(p => CalepinageGeometry.rotate(p, angleRad, origin))
    }

    static centroid(poly: Point[]): Point {
        const n = poly.length
        const sum = poly.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 })
        return { x: sum.x / n, y: sum.y / n }
    }

    static boundingBox(poly: Point[]): { minX: number; minY: number; maxX: number; maxY: number } {
        const xs = poly.map(p => p.x)
        const ys = poly.map(p => p.y)
        return {
            minX: Math.min(...xs),
            minY: Math.min(...ys),
            maxX: Math.max(...xs),
            maxY: Math.max(...ys),
        }
    }

    /**
     * Test point-in-polygon par ray casting (algo classique).
     */
    static pointInPolygon(p: Point, poly: Point[]): boolean {
        let inside = false
        const n = poly.length
        for (let i = 0, j = n - 1; i < n; j = i++) {
            const pi = poly[i]
            const pj = poly[j]
            const intersect = pi.y > p.y !== pj.y > p.y && p.x < ((pj.x - pi.x) * (p.y - pi.y)) / (pj.y - pi.y) + pi.x
            if (intersect) inside = !inside
        }
        return inside
    }

    /**
     * Distance d'un point à un segment AB.
     */
    static distancePointToSegment(p: Point, a: Point, b: Point): number {
        const dx = b.x - a.x
        const dy = b.y - a.y
        const lenSq = dx * dx + dy * dy
        if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y)
        let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq
        t = Math.max(0, Math.min(1, t))
        const proj = { x: a.x + t * dx, y: a.y + t * dy }
        return Math.hypot(p.x - proj.x, p.y - proj.y)
    }

    static distancePointToPolygon(p: Point, poly: Point[]): number {
        let min = Infinity
        const n = poly.length
        for (let i = 0; i < n; i++) {
            const d = CalepinageGeometry.distancePointToSegment(p, poly[i], poly[(i + 1) % n])
            if (d < min) min = d
        }
        return min
    }

    /**
     * Vérifie qu'un rectangle (4 coins) est entièrement à l'intérieur du polygone,
     * à au moins `minDistance` des bords.
     *
     * Cas `minDistance < 0` : tolérance de bord (remplissage maxi des ombrières). On accepte
     * un coin affleurant la frontière, voire jusqu'à `|minDistance|` au-delà — ce qu'un ray
     * casting strict rejetterait pile sur le bord. Le comportement pour `minDistance >= 0`
     * (toiture, sol) est inchangé.
     */
    static rectInsidePolygonWithMargin(rect: Point[], poly: Point[], minDistance: number): boolean {
        for (const corner of rect) {
            const inside = CalepinageGeometry.pointInPolygon(corner, poly)
            if (minDistance < 0) {
                if (!inside && CalepinageGeometry.distancePointToPolygon(corner, poly) > -minDistance) return false
            } else {
                if (!inside) return false
                if (CalepinageGeometry.distancePointToPolygon(corner, poly) < minDistance) return false
            }
        }
        return true
    }

    /**
     * Vérifie qu'un rectangle n'intersecte aucun des polygones d'exclusion (avec marge de sécurité).
     * Test : aucun coin du rectangle n'est dans une exclusion ET aucun coin de l'exclusion
     * n'est dans le rectangle (suffit pour des formes simples convexes et non-imbriquées).
     */
    static rectAvoidsExclusionsWithMargin(rect: Point[], exclusions: Point[][], minDistance: number): boolean {
        for (const excl of exclusions) {
            for (const corner of rect) {
                if (CalepinageGeometry.pointInPolygon(corner, excl)) return false
                if (CalepinageGeometry.distancePointToPolygon(corner, excl) < minDistance) return false
            }
            for (const exclCorner of excl) {
                if (CalepinageGeometry.pointInPolygon(exclCorner, rect)) return false
            }
        }
        return true
    }
}
